// Input Manager - Handles keyboard, mouse, and touch input
export class InputManager {
    constructor(canvas) {
        this.canvas = canvas;
        
        // Input states
        this.keys = {};
        this.keysPressed = {};
        this.keysReleased = {};
        this.mouse = {
            x: 0,
            y: 0,
            pressed: false,
            justPressed: false,
            justReleased: false
        };
        this.touch = {
            x: 0,
            y: 0,
            pressed: false,
            justPressed: false,
            justReleased: false
        };
        
        // Gamepad support
        this.gamepads = [];
        this.gamepadSupport = 'getGamepads' in navigator;
        
        // Touch controls for mobile
        this.touchControls = {
            joystick: {
                active: false,
                startX: 0,
                startY: 0,
                currentX: 0,
                currentY: 0,
                identifier: null
            },
            buttons: {
                jump: false,
                shoot: false,
                justPressed: new Set(),
                justReleased: new Set()
            }
        };
        
        this.setupEventListeners();
        this.startGamepadPolling();
    }

    setupEventListeners() {
        // Keyboard events
        window.addEventListener('keydown', (e) => this.handleKeyDown(e));
        window.addEventListener('keyup', (e) => this.handleKeyUp(e));
        
        // Mouse events
        this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        this.canvas.addEventListener('mouseup', (e) => this.handleMouseUp(e));
        this.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        this.canvas.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
        
        // Touch events
        this.canvas.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
        this.canvas.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: false });
        this.canvas.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
        this.canvas.addEventListener('touchcancel', (e) => this.handleTouchCancel(e), { passive: false });
        
        // Prevent context menu
        this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
        
        // Prevent default behavior for game keys
        this.preventDefaultsForGameKeys();
    }

    handleKeyDown(e) {
        const code = e.code;
        
        // Only register if not already pressed
        if (!this.keys[code]) {
            this.keys[code] = true;
            this.keysPressed[code] = true;
        }
        
        // Prevent default for game keys
        if (this.isGameKey(code)) {
            e.preventDefault();
        }
    }

    handleKeyUp(e) {
        const code = e.code;
        
        this.keys[code] = false;
        this.keysReleased[code] = true;
        
        // Prevent default for game keys
        if (this.isGameKey(code)) {
            e.preventDefault();
        }
    }

    isGameKey(code) {
        const gameKeys = [
            'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
            'KeyW', 'KeyA', 'KeyS', 'KeyD', 'Enter', 'Escape'
        ];
        return gameKeys.includes(code);
    }

    preventDefaultsForGameKeys() {
        window.addEventListener('keydown', (e) => {
            if (this.isGameKey(e.code)) {
                e.preventDefault();
            }
        });
    }

    handleMouseDown(e) {
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = e.clientX - rect.left;
        this.mouse.y = e.clientY - rect.top;
        this.mouse.pressed = true;
        this.mouse.justPressed = true;
    }

    handleMouseUp(e) {
        this.mouse.pressed = false;
        this.mouse.justReleased = true;
    }

    handleMouseMove(e) {
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = e.clientX - rect.left;
        this.mouse.y = e.clientY - rect.top;
    }

    handleMouseLeave(e) {
        this.mouse.pressed = false;
        this.mouse.justReleased = true;
    }

    handleTouchStart(e) {
        e.preventDefault();
        
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const rect = this.canvas.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            
            // Check if this is a joystick touch (left side of screen)
            if (x < this.canvas.width / 2 && !this.touchControls.joystick.active) {
                this.touchControls.joystick.active = true;
                this.touchControls.joystick.identifier = touch.identifier;
                this.touchControls.joystick.startX = x;
                this.touchControls.joystick.startY = y;
                this.touchControls.joystick.currentX = x;
                this.touchControls.joystick.currentY = y;
                
                // Update visual joystick position
                this.updateJoystickVisual(x, y);
            } else {
                // Right side - action buttons
                this.touch.pressed = true;
                this.touch.justPressed = true;
                this.touch.x = x;
                this.touch.y = y;
                
                // Check for action button presses
                this.checkActionButtonPress(x, y, true);
            }
        }
    }

    handleTouchEnd(e) {
        e.preventDefault();
        
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            
            if (touch.identifier === this.touchControls.joystick.identifier) {
                // Joystick released
                this.touchControls.joystick.active = false;
                this.touchControls.joystick.identifier = null;
                this.resetJoystickVisual();
            } else {
                // Action button released
                this.touch.pressed = false;
                this.touch.justReleased = true;
                
                // Check for action button releases
                const rect = this.canvas.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const y = touch.clientY - rect.top;
                this.checkActionButtonPress(x, y, false);
            }
        }
        
        // If no touches remain, reset everything
        if (e.touches.length === 0) {
            this.touchControls.joystick.active = false;
            this.touchControls.joystick.identifier = null;
            this.touch.pressed = false;
            this.resetJoystickVisual();
        }
    }

    handleTouchMove(e) {
        e.preventDefault();
        
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            
            if (touch.identifier === this.touchControls.joystick.identifier) {
                // Update joystick position
                const rect = this.canvas.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const y = touch.clientY - rect.top;
                
                this.touchControls.joystick.currentX = x;
                this.touchControls.joystick.currentY = y;
                
                // Update visual joystick position
                this.updateJoystickVisual(x, y);
            }
        }
    }

    handleTouchCancel(e) {
        this.handleTouchEnd(e);
    }

    updateJoystickVisual(x, y) {
        const joystickContainer = document.querySelector('.joystick-container');
        if (!joystickContainer) return;
        
        const stick = joystickContainer.querySelector('.joystick-stick');
        if (!stick) return;
        
        const baseRect = joystickContainer.querySelector('.joystick-base').getBoundingClientRect();
        const centerX = baseRect.left + baseRect.width / 2;
        const centerY = baseRect.top + baseRect.height / 2;
        
        const rect = this.canvas.getBoundingClientRect();
        const touchX = x + rect.left;
        const touchY = y + rect.top;
        
        const dx = touchX - centerX;
        const dy = touchY - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = baseRect.width / 2 - 15; // Stick radius
        
        if (distance <= maxDistance) {
            stick.style.transform = `translate(${dx}px, ${dy}px)`;
        } else {
            const angle = Math.atan2(dy, dx);
            const limitedX = Math.cos(angle) * maxDistance;
            const limitedY = Math.sin(angle) * maxDistance;
            stick.style.transform = `translate(${limitedX}px, ${limitedY}px)`;
        }
    }

    resetJoystickVisual() {
        const stick = document.querySelector('.joystick-stick');
        if (stick) {
            stick.style.transform = 'translate(0, 0)';
        }
    }

    checkActionButtonPress(x, y, pressed) {
        // Check if touch is on action buttons
        const actionButtons = document.querySelectorAll('.action-btn');
        actionButtons.forEach(btn => {
            const rect = btn.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                const action = btn.dataset.action;
                if (pressed) {
                    this.touchControls.buttons[action] = true;
                    this.touchControls.buttons.justPressed.add(action);
                } else {
                    this.touchControls.buttons[action] = false;
                    this.touchControls.buttons.justReleased.add(action);
                }
            }
        });
    }

    startGamepadPolling() {
        if (!this.gamepadSupport) return;
        
        const pollGamepads = () => {
            this.gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
            requestAnimationFrame(pollGamepads);
        };
        
        pollGamepads();
    }

    // Public API methods
    isKeyPressed(keyCode) {
        return !!this.keys[keyCode];
    }

    isKeyJustPressed(keyCode) {
        return !!this.keysPressed[keyCode];
    }

    isKeyJustReleased(keyCode) {
        return !!this.keysReleased[keyCode];
    }

    getMousePosition() {
        return { x: this.mouse.x, y: this.mouse.y };
    }

    isMousePressed() {
        return this.mouse.pressed;
    }

    isMouseJustPressed() {
        return this.mouse.justPressed;
    }

    isMouseJustReleased() {
        return this.mouse.justReleased;
    }

    getTouchPosition() {
        return { x: this.touch.x, y: this.touch.y };
    }

    isTouchPressed() {
        return this.touch.pressed;
    }

    isTouchJustPressed() {
        return this.touch.justPressed;
    }

    isTouchJustReleased() {
        return this.touch.justReleased;
    }

    getJoystickInput() {
        if (!this.touchControls.joystick.active) {
            return { x: 0, y: 0 };
        }
        
        const dx = this.touchControls.joystick.currentX - this.touchControls.joystick.startX;
        const dy = this.touchControls.joystick.currentY - this.touchControls.joystick.startY;
        const maxDistance = 50; // Normalization factor
        
        return {
            x: Math.max(-1, Math.min(1, dx / maxDistance)),
            y: Math.max(-1, Math.min(1, dy / maxDistance))
        };
    }

    isActionButtonPressed(action) {
        return this.touchControls.buttons[action] || false;
    }

    isActionButtonJustPressed(action) {
        return this.touchControls.buttons.justPressed.has(action);
    }

    isActionButtonJustReleased(action) {
        return this.touchControls.buttons.justReleased.has(action);
    }

    getGamepad(index = 0) {
        return this.gamepads[index] || null;
    }

    update() {
        // Reset just pressed/released states
        this.keysPressed = {};
        this.keysReleased = {};
        this.mouse.justPressed = false;
        this.mouse.justReleased = false;
        this.touch.justPressed = false;
        this.touch.justReleased = false;
        
        // Reset action button states
        this.touchControls.buttons.justPressed.clear();
        this.touchControls.buttons.justReleased.clear();
    }

    // Utility methods
    isMobile() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    enableTouchControls() {
        const touchControls = document.getElementById('touch-controls');
        if (touchControls) {
            touchControls.classList.remove('hidden');
        }
    }

    disableTouchControls() {
        const touchControls = document.getElementById('touch-controls');
        if (touchControls) {
            touchControls.classList.add('hidden');
        }
    }
}